import {
  CalendarOutlined,
  CheckCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  HolderOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Dropdown, Radio, Switch, Table } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useBulkDeleteBannersMutation,
  useBulkUpdateBannerStatusMutation,
  useDeleteBannerMutation,
  useGetBannersQuery,
  useReorderBannersMutation,
  useUpdateBannerMutation,
} from '@/modules/banners/bannersApi';
import type { Banner, BannerStatusFilter } from '@/types/banner';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { getBannerImageUrl } from '@/utils/media';
import { reorderById } from '@/utils/reorder';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { BannersTableSkeleton } from './components/BannersTableSkeleton';
import { BannerFormModal } from './components/BannerFormModal';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useBannerQueryParams } from './hooks/useBannerQueryParams';
import styles from './Banners.module.scss';

export default function Banners() {
  const notify = useNotify();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useBannerQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetBannersQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const banners = listResult?.results ?? [];
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [statusDraft, setStatusDraft] = useState<BannerStatusFilter>(state.status);
  const [dateDraft, setDateDraft] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    state.dateFrom || state.dateTo
      ? [state.dateFrom ? dayjs(state.dateFrom) : null, state.dateTo ? dayjs(state.dateTo) : null]
      : null,
  );

  const [updateBanner] = useUpdateBannerMutation();
  const [deleteBanner] = useDeleteBannerMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateBannerStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteBannersMutation();
  const [reorderBanners, { isLoading: isReordering }] = useReorderBannersMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const canDrag =
    state.sortBy === 'sortOrder' &&
    !state.search &&
    state.status === 'all' &&
    !state.dateFrom &&
    !state.dateTo;

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const openCreate = () => {
    setEditingBanner(null);
    setModalOpen(true);
  };

  const openEdit = useCallback((banner: Banner) => {
    setEditingBanner(banner);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingBanner(null);
  };

  const clearSelection = () => setSelectedRowKeys([]);

  const handleResetFiltersAndSort = () => {
    setStatusDraft('all');
    setDateDraft(null);
    resetAll();
  };

  const handleStatusChange = useCallback(
    async (banner: Banner, isActive: boolean) => {
      const result = await resolveMutationResult(
        updateBanner({ id: banner.id, body: { isActive } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateBanner],
  );

  const handleDelete = useCallback(
    async (banner: Banner) => {
      const confirmed = await confirmDelete(banner.link || 'this banner');
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteBanner(banner.id));
      if (result.ok) {
        notify.success(result.message ?? 'Banner deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== banner.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteBanner, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (selectedRowKeys.length === 0) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Banners updated.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));
    if (result.ok) {
      notify.success(result.message ?? 'Banners deleted.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleDrop = async (dropId: string) => {
    if (!canDrag || !dragId || dragId === dropId) {
      setDragId(null);
      return;
    }

    const reordered = reorderById(banners, dragId, dropId);
    setDragId(null);

    const result = await resolveMutationResult(
      reorderBanners({ ids: reordered.map((item) => item.id) }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Order updated.');
      return;
    }

    notify.error(result.message);
  };

  const handleTableChange: TableProps<Banner>['onChange'] = (pagination) => {
    const pageChanged =
      pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged =
      pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (banner: Banner): MenuProps['items'] => [
      { key: 'edit', label: 'Edit', onClick: () => openEdit(banner) },
      { type: 'divider', key: 'divider' },
      { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDelete(banner) },
    ],
    [handleDelete, openEdit],
  );

  const columns: ColumnsType<Banner> = useMemo(
    () => [
      {
        title: '',
        key: 'drag',
        width: 40,
        className: styles.dragCol,
        render: () => (canDrag ? <HolderOutlined className={styles.dragHandle} /> : null),
      },
      {
        title: 'IMAGE',
        dataIndex: 'image',
        key: 'image',
        align: 'center',
        className: styles.imageCol,
        render: (_image: string | undefined, record) => (
          <PreviewImage
            src={getBannerImageUrl(record.image)}
            size={48}
            className={styles.thumb}
            fallback={<PictureOutlined />}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ status: statusDraft === 'all' ? null : statusDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            onChange={(checked) => handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="SORT"
            field="sortOrder"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        align: 'center',
        render: (value: number) => (
          <span className={styles.sortNum}>
            {Number.isFinite(Number(value)) ? Number(value) + 1 : '—'}
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        filterIcon: (filtered) => (
          <CalendarOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.dateFrom || state.dateTo ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                {
                  dateFrom: dateDraft?.[0]?.format('YYYY-MM-DD') || null,
                  dateTo: dateDraft?.[1]?.format('YYYY-MM-DD') || null,
                },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setDateDraft(null);
              patchParams({ dateFrom: null, dateTo: null }, { resetPage: true });
              close();
            }}
          >
            <DatePicker.RangePicker
              size="small"
              value={dateDraft}
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => setDateDraft(values)}
              style={{ width: '100%' }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setDateDraft(
                state.dateFrom || state.dateTo
                  ? [
                      state.dateFrom ? dayjs(state.dateFrom) : null,
                      state.dateTo ? dayjs(state.dateTo) : null,
                    ]
                  : null,
              );
            }
          },
        },
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              icon={<EllipsisOutlined />}
              aria-label="Open actions"
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [handleSort, activeSortBy, activeSortOrder, state.status, state.dateFrom, state.dateTo, canDrag, patchParams, statusDraft, handleStatusChange, dateDraft, getRowActionItems],
  );

  const rowSelection: TableRowSelection<Banner> = {
    columnWidth: 40,
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  const showSkeleton = isLoading && !listResult;
  const mutationLoading = isReordering || isBulkUpdating || isBulkDeleting;
  const hasSelection = selectedRowKeys.length > 0;

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFiltersAndSort}>
                Reset
              </Button>
            ) : null}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add banner
            </Button>
          </>
        }
      />

      {hasSelection ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={handleBulkDelete} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        {showSkeleton ? (
          <BannersTableSkeleton rows={pageSize} />
        ) : (
          <Table<Banner>
            className={styles.table}
            rowKey="id"
            size="small"
            loading={mutationLoading}
            columns={columns}
            dataSource={banners}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
              showTotal: (total, [start, end]) => `${start}-${end} of ${total}`,
              size: 'small',
            }}
            onRow={(record) => ({
              draggable: canDrag,
              className: dragId === record.id ? styles.draggingRow : undefined,
              onDragStart: () => setDragId(record.id),
              onDragEnd: () => setDragId(null),
              onDragOver: (event) => event.preventDefault(),
              onDrop: () => handleDrop(record.id),
            })}
          />
        )}
      </div>

      <BannerFormModal open={modalOpen} banner={editingBanner} onClose={closeModal} />
    </div>
  );
}
